import { useCallback, useEffect, useState } from "react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { 
  $getSelection, 
  $isRangeSelection, 
  SELECTION_CHANGE_COMMAND,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  $isElementNode
} from "lexical"
import { $isListNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND } from "@lexical/list"
import { $findMatchingParent } from "@lexical/utils"

export function FormattingToolbar() {
  const [editor] = useLexicalComposerContext()
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)
  const [elementFormat, setElementFormat] = useState<string>('')
  const [isList, setIsList] = useState(false)

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      // Update text formatting
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsUnderline(selection.hasFormat('underline'))
      setIsStrikethrough(selection.hasFormat('strikethrough'))

      // Update element format (alignment)
      const anchorNode = selection.anchor.getNode()
      const element = anchorNode.getKey() === 'root' 
        ? anchorNode 
        : $findMatchingParent(anchorNode, (e) => {
            const parent = e.getParent()
            return parent !== null && parent.getKey() === 'root'
          })

      if (element && $isElementNode(element)) {
        setElementFormat(element.getFormatType())
        setIsList($isListNode(element))
      }
    }
  }, [])

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        $updateToolbar()
        return false
      },
      COMMAND_PRIORITY_CRITICAL
    )
  }, [editor, $updateToolbar])

  const formatText = (format: 'bold' | 'italic' | 'underline' | 'strikethrough') => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)
  }

  const formatElement = (format: 'left' | 'center' | 'right' | 'justify') => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, format)
  }

  const insertList = (listType: 'bullet' | 'number') => {
    if (isList) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
    } else if (listType === 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    }
  }

  return (
    <div className="border-b border-gray-200 p-2 flex gap-1 flex-wrap bg-gray-50">
      {/* Text Formatting */}
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => formatText('bold')}
          className={`px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 font-bold ${
            isBold ? 'bg-blue-100 border-blue-300' : ''
          } focus:outline-none focus:ring-0`}
        >
          B
        </button>
        <button
          type="button"
          onClick={() => formatText('italic')}
          className={`px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 italic ${
            isItalic ? 'bg-blue-100 border-blue-300' : ''
          } focus:outline-none focus:ring-0`}
        >
          I
        </button>
        <button
          type="button"
          onClick={() => formatText('underline')}
          className={`px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 underline ${
            isUnderline ? 'bg-blue-100 border-blue-300' : ''
          } focus:outline-none focus:ring-0`}
        >
          U
        </button>
        <button
          type="button"
          onClick={() => formatText('strikethrough')}
          className={`px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 line-through ${
            isStrikethrough ? 'bg-blue-100 border-blue-300' : ''
          } focus:outline-none focus:ring-0`}
        >
          S
        </button>
      </div>

      <div className="border-l border-gray-300 mx-2"></div>

      {/* Alignment */}
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => formatElement('left')}
          className={`px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 ${
            elementFormat === 'left' || elementFormat === '' ? 'bg-blue-100 border-blue-300' : ''
          } focus:outline-none focus:ring-0`}
          title="Align Left"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="15" y2="12"/>
            <line x1="3" y1="18" x2="18" y2="18"/>
          </svg>
        </button>
        <button
          type="button"
          onClick={() => formatElement('center')}
          className={`px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 ${
            elementFormat === 'center' ? 'bg-blue-100 border-blue-300' : ''
          } focus:outline-none focus:ring-0`}
          title="Align Center"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="6" y1="12" x2="18" y2="12"/>
            <line x1="4" y1="18" x2="20" y2="18"/>
          </svg>
        </button>
        <button
          type="button"
          onClick={() => formatElement('right')}
          className={`px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 ${
            elementFormat === 'right' ? 'bg-blue-100 border-blue-300' : ''
          } focus:outline-none focus:ring-0`}
          title="Align Right"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="9" y1="12" x2="21" y2="12"/>
            <line x1="6" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <button
          type="button"
          onClick={() => formatElement('justify')}
          className={`px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 ${
            elementFormat === 'justify' ? 'bg-blue-100 border-blue-300' : ''
          } focus:outline-none focus:ring-0`}
          title="Justify"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
      </div>

      <div className="border-l border-gray-300 mx-2"></div>

      {/* Lists */}
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => insertList('bullet')}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 focus:outline-none focus:ring-0"
          title="Bullet List"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="6" cy="6" r="1.5" fill="currentColor"/>
            <circle cx="6" cy="12" r="1.5" fill="currentColor"/>
            <circle cx="6" cy="18" r="1.5" fill="currentColor"/>
            <line x1="11" y1="6" x2="21" y2="6"/>
            <line x1="11" y1="12" x2="21" y2="12"/>
            <line x1="11" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <button
          type="button"
          onClick={() => insertList('number')}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 focus:outline-none focus:ring-0"
          title="Numbered List"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="10" y1="6" x2="21" y2="6"/>
            <line x1="10" y1="12" x2="21" y2="12"/>
            <line x1="10" y1="18" x2="21" y2="18"/>
            <path d="M4 6h1v4"/>
            <path d="M4 10h2"/>
            <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/>
          </svg>
        </button>
      </div>
    </div>
  )
}
